import type { KeyboardEvent } from 'react';
import { Box, CircularProgress, Switch, SwitchProps, styled } from '@mui/material';
import { purple } from '@mui/material/colors';
import { withStyles } from '@mui/styles';

export const PurpleSwitch = withStyles({
    switchBase: {
        color: purple[300],
        '&$checked': {
            color: purple[500],
        },
        '&$checked + $track': {
            backgroundColor: purple[500],
        },
    },
    checked: {},
    track: {},
})(Switch);

/** 轨道总宽（略宽于 MUI 文档 42px，滑动行程更长） */
const IOSS_SWITCH_WIDTH_PX = 60;
/** 轨道总高（略高于文档 26px，更易点按） */
const IOSS_SWITCH_HEIGHT_PX = 30;
/** 拇指直径（与轨道高、边距协调，保持 iOS 式包在轨内） */
const IOSS_SWITCH_THUMB_PX = 26;
/** switchBase 四边留白，使拇指落在轨道内 */
const IOSS_SWITCH_BASE_MARGIN_PX = 2;
/** 选中态水平位移 = 宽 − 两侧 margin − thumb */
const IOSS_SWITCH_TRANSLATE_X_PX =
    IOSS_SWITCH_WIDTH_PX - 2 * IOSS_SWITCH_BASE_MARGIN_PX - IOSS_SWITCH_THUMB_PX;
/** loading 环与拇指中心对齐（相对根节点左缘） */
const IOSS_SWITCH_THUMB_CENTER_OFF_PX = IOSS_SWITCH_BASE_MARGIN_PX + IOSS_SWITCH_THUMB_PX / 2;
const IOSS_SWITCH_THUMB_CENTER_ON_PX =
    IOSS_SWITCH_BASE_MARGIN_PX + IOSS_SWITCH_TRANSLATE_X_PX + IOSS_SWITCH_THUMB_PX / 2;

/**
 * iOS 比例开关（轨道高度包住 thumb）；开启态主题主色；可选 loading 在 thumb 中心
 */
const StyledIOSSwitch = styled(Switch)(({ theme }) => ({
    width: IOSS_SWITCH_WIDTH_PX,
    height: IOSS_SWITCH_HEIGHT_PX,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: IOSS_SWITCH_BASE_MARGIN_PX,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: `translateX(${IOSS_SWITCH_TRANSLATE_X_PX}px)`,
            color: theme.palette.common.white,
            '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.main,
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 1,
            },
        },
        /* 较细的焦点环，避免出现过大的「光晕」块 */
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: theme.palette.primary.main,
            border: `3px solid ${theme.palette.common.white}`,
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey[100],
            ...(theme.palette.mode === 'dark' ? { color: theme.palette.grey[600] } : {}),
        },
        /** 禁用态轨道：图标/辅助色-800（含未选中与选中） */
        '&.Mui-disabled + .MuiSwitch-track': {
            backgroundColor: 'var(--icon-neutral-800)',
            opacity: 1,
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: IOSS_SWITCH_THUMB_PX,
        height: IOSS_SWITCH_THUMB_PX,
        boxShadow:
            theme.palette.mode === 'dark'
                ? '0 1px 2px rgba(0,0,0,0.45)'
                : '0 1px 2px rgba(0,0,0,0.12)',
    },
    /** 关闭态轨道：图标/按钮-辅助色-500（亮暗由 token 适配） */
    '& .MuiSwitch-track': {
        borderRadius: IOSS_SWITCH_HEIGHT_PX / 2,
        backgroundColor: 'var(--icon-neutral-500)',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

export type IOSSwitchProps = SwitchProps & {
    /** 为 true 时在滑块中心显示加载动画；通过遮罩与键盘拦截阻止切换，不使用 MUI disabled 以免变灰 */
    loading?: boolean;
};

/**
 * @param props 透传至底层 Switch（含 className、checked、onChange 等）
 */
export function IOSSwitch(props: IOSSwitchProps) {
    const { loading, disabled, checked, tabIndex, ...rest } = props;
    const thumbCenterX = checked === true ? IOSS_SWITCH_THUMB_CENTER_ON_PX : IOSS_SWITCH_THUMB_CENTER_OFF_PX;

    // loading 时 tabIndex=-1：新焦点不会进开关；已聚焦时由外层 onKeyDownCapture 拦空格/回车
    const switchNode = (
        <StyledIOSSwitch
            disableRipple
            disabled={Boolean(disabled)}
            checked={checked}
            {...rest}
            tabIndex={loading ? -1 : tabIndex}
        />
    );

    if (!loading) {
        return switchNode;
    }

    /**
     * 加载态拦截空格/回车（透明遮罩挡鼠标；若焦点已在内层，capture 仍可拦住默认切换）
     * @param e - 来自外层包装的原生键盘事件
     */
    const blockToggleKeysCapture = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== ' ' && e.key !== 'Enter') {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
            }}
            aria-busy="true"
            aria-live="polite"
            onKeyDownCapture={blockToggleKeysCapture}
        >
            {switchNode}
            {/* 全区域命中层：loading 时不走 MUI disabled，靠遮罩阻止点击穿透 */}
            <Box
                aria-hidden
                sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    cursor: 'default',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    zIndex: 2,
                    pointerEvents: 'none',
                    top: '50%',
                    left: thumbCenterX,
                    transform: 'translate(-50%, -50%)',
                    width: IOSS_SWITCH_THUMB_PX,
                    height: IOSS_SWITCH_THUMB_PX,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress size={18} thickness={5} sx={{ color: 'var(--fill-primary-300)' }} />
            </Box>
        </Box>
    );
}

export const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 80,
    height: 40,
    padding: 0,
    display: 'flex',
    '&:active': {
        '& .MuiSwitch-thumb': {
            width: 36,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            transform: 'translateX(36px)',
        },
    },
    '& .MuiSwitch-switchBase': {
        padding: 4,
        '&.Mui-checked': {
            transform: 'translateX(40px)',
            color: 'var(--fill-neutral-100)',
            '& + .MuiSwitch-track': {
                backgroundColor: 'var(--text-primary-300)',
                opacity: 1,
            },
        },
        /** 禁用态轨道：图标/辅助色-800 */
        '&.Mui-disabled + .MuiSwitch-track': {
            backgroundColor: 'var(--icon-neutral-800)',
        },
    },
    '& .MuiSwitch-thumb': {
        width: 32,
        height: 32,
        borderRadius: 16,
        transition: theme.transitions.create(['width'], {
            duration: 200,
        }),
        backgroundColor: 'var(--fill-neutral-100)',
        boxShadow: '0 2px 4px var(--shadow-25)',
    },
    /** 关闭态轨道：图标/按钮-辅助色-500 */
    '& .MuiSwitch-track': {
        borderRadius: 24,
        backgroundColor: 'var(--icon-neutral-500)',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));
